import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import AskKundliModal from "@/components/AskKundliModal";
import AskPalmistryModal from "@/components/AskPalmistryModal";
import { ArrowLeft, Orbit, Hand, ShieldAlert, HeartPulse, ChevronRight, BookOpen, ScrollText, Sparkles, Loader2 } from "lucide-react";

export default function QuestionDetail() {
  const { qid } = useParams();
  const { t, lang, user } = useApp();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [askKundli, setAskKundli] = useState(false);
  const [askPalm, setAskPalm] = useState(false);

  const pick = (en, hi) => (lang === "hi" ? hi : en);

  useEffect(() => {
    setLoading(true);
    api.get(`/qa/item/${qid}`)
      .then(({ data: res }) => {
        setData(res.item);
        setRelated(res.related);
        const q = lang === "hi" ? res.item.question_hi : res.item.question_en;
        document.title = `${q} | AstroFriend`;
        let meta = document.querySelector('meta[name="description"]');
        if (!meta) { meta = document.createElement("meta"); meta.name = "description"; document.head.appendChild(meta); }
        meta.content = lang === "hi" ? res.item.short_hi : res.item.short_en;
        window.scrollTo(0, 0);
      })
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [qid, lang]);

  if (loading) return <div className="min-h-[50vh] grid place-items-center text-amber-200"><Loader2 className="animate-spin" /></div>;
  if (!data) return <div className="min-h-[50vh] grid place-items-center text-slate-300">404</div>;

  const isPalmistry = data.category === "palmistry";
  const question = pick(data.question_en, data.question_hi);

  const Section = ({ icon: Icon, title, children, color = "#FBBF24" }) => (
    <div className="glass rounded-2xl p-6">
      <h2 className="font-serif text-lg mb-3 flex items-center gap-2" style={{ color }}>
        <Icon size={18} /> {title}
      </h2>
      <div className="text-slate-200 leading-relaxed">{children}</div>
    </div>
  );

  return (
    <div className="mx-auto max-w-3xl px-6 py-10">
      <Link to="/library" data-testid="back-to-library" className="inline-flex items-center gap-2 text-sm text-amber-300/80 hover:text-amber-200 mb-6">
        <ArrowLeft size={16} /> {t.detail.back}
      </Link>

      <article>
        <span className="inline-block text-xs uppercase tracking-widest text-purple-300 mb-3">
          {t.library.filters[data.filters?.[0]] || data.category}
        </span>
        <h1 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-bold text-amber-50 leading-tight mb-6" data-testid="qa-question-title">
          {question}
        </h1>

        <div className="space-y-4">
          <Section icon={Sparkles} title={t.detail.shortAnswer} color="#34D399">
            <p className="font-medium">{pick(data.short_en, data.short_hi)}</p>
          </Section>
          <Section icon={BookOpen} title={t.detail.detailed} color="#60A5FA">
            <p>{pick(data.detailed_en, data.detailed_hi)}</p>
          </Section>
          <Section icon={ScrollText} title={t.detail.traditional} color="#C084FC">
            <p>{pick(data.traditional_en, data.traditional_hi)}</p>
          </Section>
        </div>

        {/* Related topics + keywords */}
        <div className="mt-6 grid sm:grid-cols-2 gap-4">
          <div>
            <div className="text-xs uppercase tracking-widest text-amber-300/70 mb-2">{t.detail.relatedTopics}</div>
            <div className="flex flex-wrap gap-2">
              {pick(data.related_en, data.related_hi)?.map((r) => (
                <span key={r} className="text-xs px-3 py-1 rounded-full bg-amber-500/10 text-amber-200 border border-amber-500/25">{r}</span>
              ))}
            </div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-widest text-amber-300/70 mb-2">{t.detail.keywords}</div>
            <div className="flex flex-wrap gap-2">
              {data.keywords?.map((k) => (
                <span key={k} className="text-xs px-3 py-1 rounded-full bg-purple-800/40 text-purple-200 border border-purple-500/20">{k}</span>
              ))}
            </div>
          </div>
        </div>

        {/* Ask AI CTAs */}
        <div className="mt-8 grid gap-3 sm:grid-cols-2">
          <button onClick={() => (user ? setAskKundli(true) : navigate("/login"))} data-testid="ask-kundli-btn"
            className="btn-gold rounded-2xl px-5 py-4 flex items-center justify-center gap-2 gold-glow">
            <Orbit size={18} /> {t.detail.askKundli}
          </button>
          {isPalmistry && (
            <button onClick={() => (user ? setAskPalm(true) : navigate("/login"))} data-testid="ask-palmistry-btn"
              className="btn-ghost-gold rounded-2xl px-5 py-4 flex items-center justify-center gap-2">
              <Hand size={18} /> {t.detail.askPalmistry}
            </button>
          )}
        </div>

        {/* Disclaimers */}
        <div className="mt-8 space-y-3">
          {data.health && (
            <div className="glass rounded-xl p-4 border border-rose-500/30 flex gap-3" data-testid="health-disclaimer">
              <HeartPulse className="text-rose-300 shrink-0" size={18} />
              <p className="text-sm text-rose-200/90">{t.detail.healthDisclaimer}</p>
            </div>
          )}
          <div className="glass rounded-xl p-4 border border-amber-500/20 flex gap-3" data-testid="general-disclaimer">
            <ShieldAlert className="text-amber-300 shrink-0" size={18} />
            <p className="text-sm text-slate-400">{t.detail.disclaimer}</p>
          </div>
        </div>
      </article>

      {/* Related questions */}
      {related.length > 0 && (
        <div className="mt-12">
          <h2 className="font-serif text-xl text-amber-200 mb-4">{t.detail.relatedQuestions}</h2>
          <div className="grid gap-3">
            {related.map((r) => (
              <Link key={r.qid} to={`/library/${r.qid}`} data-testid={`related-${r.qid}`}
                className="glass rounded-xl p-4 flex items-center gap-3 hover:scale-[1.01] transition-transform">
                <span className="flex-1 text-slate-200">{pick(r.question_en, r.question_hi)}</span>
                <ChevronRight className="text-slate-500 shrink-0" size={18} />
              </Link>
            ))}
          </div>
        </div>
      )}

      {askKundli && <AskKundliModal question={question} onClose={() => setAskKundli(false)} />}
      {askPalm && <AskPalmistryModal question={question} onClose={() => setAskPalm(false)} />}
    </div>
  );
}
